import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Pagination from '../Pagination/Pagination';
import ListSearchField from './ListSearchField';
import app from '@src/helpers/app';
import Table from './Table';
import classnames from '@src/helpers/classnames';
import TableHead from './TableHead';
import TableBody from './TableBody';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {ListContentProps, TableBodyMouseEventHandler} from '@src/@types/components/List';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import {UserPreferenceEvents} from '@src/@types/services/Auth/UserPreferenceEvents';
import {UserPreferencesKeys} from '@src/@types/services/UserPreferencesNamesResolver';
import Translate from '@src/components/Translate/Translate';

const emptyObject = {};

const sizeSmProps = {size: 'sm'};

function renderComponent(component: any | undefined, props: any) {
    // noinspection UnnecessaryLocalVariableJS
    const Tag = component;
    if (!Tag) {
        return null;
    }

    return <Tag {...props}>{props?.children}</Tag>;
}

export default function ListContent<Model extends IEmptyModel = IEmptyModel>(props: ListContentProps<Model>) {
    const listConfigContext = useContext(ListConfigContext);
    const navigate = useNavigate();

    const canOpenModelShowPage = props.canOpenModelShowPage;
    const listConfig = listConfigContext ? app('config-maker').makeListConfig(
            listConfigContext.controllerName,
            listConfigContext.config
    ) : null;

    const [, setLastUpdateDate] = useState<number>(Date.now());
    useEffect(() => {
        const subscription = app('events').listen(UserPreferenceEvents.SAVED, () => {
            setLastUpdateDate(Date.now());
        });

        return () => subscription.unsubscribe();
    }, []);

    const isIsolated = props.isIsolatedComponent === true;
    const refetch = props.customRefetch ?? props.queryResult.refetch;
    const toggleFiltersVisibilityCallback = props.toggleFiltersVisibility;
    const [onPageChange, toggleFiltersVisibility] = useMemo(() => {
        const onPageChange = isIsolated ? (page: number) => {
            listConfigContext?.setCurrentPage(page);
        } : undefined;

        const toggleFiltersVisibility = toggleFiltersVisibilityCallback ?
                () => toggleFiltersVisibilityCallback() :
                undefined;

        return [onPageChange, toggleFiltersVisibility];
    }, [isIsolated, toggleFiltersVisibilityCallback, listConfigContext]);

    const openModelShowPage = (inNewTab: boolean): TableBodyMouseEventHandler<Model> =>
            (e: React.MouseEvent, model: Model | undefined, rowIndex: number): void => {
                if (!listConfig || !model || !canOpenModelShowPage()) {
                    return;
                }

                if (props.onRowClicked) {
                    return props.onRowClicked(rowIndex, model, inNewTab);
                }

                const url = listConfig.getUrlOfModelShowPage(model.id as number);
                if (!url) {
                    return;
                }

                if (inNewTab && window && window.open) {
                    window.open(url, '_blank');

                    return;
                }

                navigate(url, {state: {model}});
            };

    const {
        withoutPagination,
        withoutControlsRow,
        withoutSearchField,
        withoutFilters,
        keepWrapperInBox,
        filteringDisabled,
        enableFiltering,
        chosenFilterName,
        chosenFilterId,
    } = props;

    const user = app('auth').getUser();
    const filterChosenByProps = (chosenFilterName ?? chosenFilterId) != null;
    const filteringEnabled = filterChosenByProps || (
            !withoutPagination &&
            (
                    !listConfigContext ||
                    (user?.getModelPreferenceValue(
                            listConfigContext.controllerName,
                            UserPreferencesKeys.LIST_FILTERING_ENABLED
                    ) ?? true)
            )
    );
    const displayPagination = (
            !withoutPagination &&
            (
                    !listConfigContext ||
                    !user?.getModelPreferenceValue(
                            listConfigContext.controllerName,
                            UserPreferencesKeys.LIST_PAGINATION_DISABLED
                    )
            )
    );

    const response = props.customPaginationData ?? props.queryResult.data;
    const paginator = displayPagination ? (
            <Pagination
                    response={response}
                    isIsolatedComponent={props.isIsolatedComponent}
                    onClick={onPageChange}
                    pageNumberGetParamName={props.pageNumberGetParamName}
            />
    ) : null;
    const isClickableList = response && canOpenModelShowPage();

    const displayFilters = (
            toggleFiltersVisibility != null &&
            listConfig?.hasFilterableColumns() === true
    );

    const chosenFilter = listConfigContext ? user?.getModelPreferenceValue(
            listConfigContext.controllerName,
            UserPreferencesKeys.LIST_CHOSEN_FILTER,
    ) : null;
    const currentFilter = props.queryResult.data?.allowedFilters?.find(filter => {
        if (chosenFilterId != null) {
            return filter.id === chosenFilterId;
        }

        if (chosenFilterName != null) {
            return filter.name === chosenFilterName;
        }

        return filter.id === chosenFilter;
    });
    const filledOptionsNumber = Object.values(currentFilter?.payload ?? {}).filter(f => {
        return Array.isArray(f.value) ?
                (f.value.length > 0) :
                (f.value !== null);
    }).length;

    return <div className="models-list">
        {(withoutControlsRow || (!displayFilters && withoutSearchField) && (!displayPagination && !props.createButton)) ? null : (
                <div className={classnames('models-list__before-table-controls', {
                    'models-list__before-table-controls--without-filters-visibility-button': !displayFilters,
                })}>
                    {(withoutFilters && withoutSearchField) ? null : (
                            <div className={classnames('models-list__before-table-controls__row', {
                                'models-list__before-table-controls__row--filtering-force-disabled': filteringDisabled
                            })}>
                                {filteringDisabled ? <>
                                    <div className="models-list__filtering-force-disabled-message">
                                        <Translate message="List.filters-were-forcibly-disabled.message"/>
                                        {enableFiltering ? (
                                                <Translate<HTMLButtonElement>
                                                        message="List.filters-were-forcibly-disabled.enable-button-label"
                                                        tag="button"
                                                        type="button"
                                                        className="btn btn-sm btn-outline-success ms-2"
                                                        onClick={enableFiltering}
                                                />
                                        ) : null}
                                    </div>
                                </> : <>
                                    {(!withoutFilters && displayFilters) ? (
                                            <button
                                                    type="button"
                                                    className={classnames('models-list__filters-visibility-button', {
                                                        'models-list__filters-visibility-button--active': props.filtersAreVisible,
                                                    }, 'position-relative')}
                                                    onClick={toggleFiltersVisibility}
                                                    disabled={filteringDisabled}
                                            >
                                                <i className="fas fa-filter"></i>
                                                {(filteringEnabled && filledOptionsNumber && !filteringDisabled) ? (
                                                        <span className={classnames([
                                                            'badge position-absolute top-0',
                                                            'start-100 translate-middle',
                                                            'rounded-pill',
                                                            'bg-danger text-bg-danger',
                                                        ])}>{filledOptionsNumber}</span>
                                                ) : null}
                                            </button>
                                    ) : null}

                                    {withoutSearchField ? null : (
                                            <ListSearchField
                                                    inputGroupProps={sizeSmProps}
                                                    placeholder="List.search-field-placeholder"
                                                    className="models-list__search-field"
                                                    defaultValue={props.searchTerm}
                                                    onSubmit={props.saveSearchTerm}
                                            />
                                    )}
                                </>}
                            </div>
                    )}

                    {(!displayPagination && !props.createButton) ? null : (
                            <div className="models-list__before-table-controls__row">
                                {!displayPagination ? null : (
                                        <div className="models-list__pagination-wrapper models-list__pagination-wrapper--top">
                                            {renderComponent(props.renderComponentBeforeTheTopPagination, response)}
                                            <div className="models-list__pagination">{paginator}</div>
                                            {renderComponent(props.renderComponentAfterTheTopPagination, response)}
                                        </div>
                                )}

                                {!props.createButton ? null : <div>{
                                    typeof props.createButton === 'function' ?
                                            props.createButton() :
                                            props.createButton
                                }</div>}
                            </div>
                    )}
                </div>
        )}


        <div className={classnames('models-list__content-table__wrapper', {
            'models-list__content-table__wrapper--keep-in-box': keepWrapperInBox
        })}>
            <Table
                    responsive
                    hover
                    {...(props.tableProps ?? emptyObject)}
                    className={classnames('models-list__content-table', {
                        'models-list__content-table--clickable': isClickableList,
                    }, props.tableProps?.className)}
            >
                <TableHead
                        {...(props.tableHeadProps ?? emptyObject)}
                        sortBy={props.sortBy}
                        saveSortingParams={props.saveSortingParams}
                        refreshRecords={refetch}
                        uniqueListName={props.uniqueListName}
                        listSettingsControllerName={props.listSettingsControllerName}
                        columns={listConfigContext?.columns}
                        withoutListSettings={props.withoutListSettings}
                />

                <TableBody
                        {...(props.tableBodyProps ?? emptyObject)}
                        nowLoading={props.queryResult.isLoading || props.isLoading}
                        isPaused={props.queryResult.isPaused}
                        error={props.queryResult.error}
                        records={props.listOfRecords ?? response?.data}
                        onMiddleClick={openModelShowPage(true)}
                        onLeftClick={openModelShowPage(false)}
                        canInteractWithRows={canOpenModelShowPage()}
                        refreshRecords={refetch}
                        uniqueListName={props.uniqueListName}
                />

                {props.tableCaptionNode}
            </Table>
        </div>

        {displayPagination ? (
                <div className="models-list__pagination-wrapper models-list__pagination-wrapper--bottom">
                    {renderComponent(props.renderComponentBeforeTheBottomPagination, response)}
                    <div className="models-list__pagination">{paginator}</div>
                    {renderComponent(props.renderComponentAfterTheBottomPagination, response)}
                </div>
        ) : null}
    </div>;
}
